import {
  Component,
  Host,
  h,
  State,
  Event,
  EventEmitter,
  Prop,
  Listen,
  Element,
} from '@stencil/core';
import {
  DateUtil,
  SessionModel,
  SessionHelper,
  Language,
  SessionStayPeriod,
  SessionGuests,
} from '@cesare';
import { QwButton } from '../shared/qw-button/qw-button';
import { cloneDeep } from 'lodash-es';
import {
  DEFAULT_LOCATION,
  QW_ROOM_TYPE_LIST,
  QwBookinBarInputType,
  QwLocation,
  QwRoomTypeType,
  LOCATIONS,
} from '../../index';

import { getPromoCodeValueFromQueryParams } from '../../core/promo-code/promo-code.service';
import {
  getSessionSettingsFromSessionStorage,
  sessionStorageGetItem,
  sessionStorageSetItem,
  removeFusionIdFromSessionStorage,
} from '../../core/storage/storage.service';

import {
  createDeepLink,
  getSessionFromSessionStorageOrEnv,
  isBookingAccommodationSatisfied,
  isBookingInProgress,
  isInFunnelPage,
  isInGroup,
  setBookingInProgress,
  setSessionSettingsToSessionStorage,
} from '../../core/app/app';
import { isRunningLocally } from '../../core/utils';
import { IS_MOBILE } from '../../global/app';

export interface QwGuestUpdate {
  isChanged: boolean;
  sessionGuest: SessionGuests;
}
export interface QwCalendarUpdate {
  isChanged: boolean;
  stayPeriod: SessionStayPeriod;
}

@Component({
  tag: 'qw-booking-bar',
  styleUrl: 'qw-booking-bar.scss',
  shadow: false,
})
export class QwBookinBar {
  @Element() el: HTMLElement;
  @Prop() qwBookingBarMessage?: string;
  @Prop() qwBookingBarDefaultLocation: any;
  @State() isAccommodationSatisfy: boolean;
  @State() roomTypeLabel: QwRoomTypeType['label'];
  @Event() qwOpenBookigModal: EventEmitter<string>;
  @Prop() currentLang = sessionStorageGetItem('lang_js') || 'en';
  @State() isBooked: boolean = false;
  @State() sessionSettings: any;
  @State() selectedDates: string;
  @State() selectedGuests: string;
  @State() tempGuests: SessionGuests;
  @State() tempDates: SessionStayPeriod;
  @State() selectedLocationName: string;
  @State() selectedLocationValue: string;
  @State() session: SessionModel;
  @State() sessionToSend: SessionModel;
  @Event() qwEmitGtmData: EventEmitter<any>;

  public componentWillLoad() {
    this.session = getSessionFromSessionStorageOrEnv();
    this.sessionToSend = getSessionFromSessionStorageOrEnv();
    this.sessionSettings = getSessionSettingsFromSessionStorage();
    this.tempGuests = this.session.context.guests;
    this.tempDates = this.session.context.stayPeriod;
    this.selectedDates =
      SessionHelper.formatStayPeriod(this.session) ||
      Language.getTranslation('whereAreYouGoing');
    this.selectedGuests = this.getGuestsLabel(this.session);

    const promoCodeValue = getPromoCodeValueFromQueryParams();
    if (promoCodeValue) {
      sessionStorageSetItem('promoCode', promoCodeValue);
    }
    this.qwBookingBarDefaultLocation = this.qwBookingBarDefaultLocation
      ? JSON.parse(this.qwBookingBarDefaultLocation)
      : LOCATIONS;

    const selectedLocation = this.qwBookingBarDefaultLocation.hostel.find(
      (h) => h.hotel_id == this.session.context.selectedHotelId,
    );

    if (selectedLocation && !isInGroup()) {
      this.selectedLocationName = selectedLocation?.name;
      this.selectedLocationValue = selectedLocation?.value;
    } else {
      this.selectedLocationName = '';
      this.selectedLocationValue = 'root';
    }

    this.roomTypeLabel =
      this.getRoomTypeLabel(sessionStorageGetItem('roomType')) ||
      Language.getTranslation('allRooms');
    isInGroup() ? sessionStorage.removeItem('bookingProgress') : '';
    isInGroup() ? sessionStorage.removeItem('sessionSettings') : '';
    this.isBooked = isBookingInProgress();
    this.isAccommodationSatisfy = isBookingAccommodationSatisfied();

    if (!IS_MOBILE) {
      document.body.addEventListener('click', this.handleClickOutside);
      window.addEventListener('scroll', this.handleComponentDisplay);
    }
  }

  private handleClickOutside = (event: MouseEvent) => {
    if (!this.el.contains(event.target as Node)) {
      this.closeComponents();
    }
  };
  private handleComponentDisplay = () => {
    if (window.scrollY > 200) {
      this.closeComponents();
    }
    const bookingPosition =
      window.scrollY + this.el.getBoundingClientRect().top;

    if (window.scrollY > bookingPosition - window.innerHeight / 3) {
      this.el.classList.add('be-component--bottom');
    } else if (this.el.classList.contains('be-component--bottom')) {
      this.el.classList.remove('be-component--bottom');
    }
  };

  private getGuestsLabel(session: SessionModel) {
    return (
      `${SessionHelper.getTotalGuests(session)} ${this.getSingularLabel(
        SessionHelper.getTotalGuests(session),
      )}` || Language.getTranslation('guests')
    );
  }

  @Listen('changeLocationLink', { target: 'window' })
  public onQwChangeLocationLink(e: CustomEvent<QwLocation>) {
    this.selectedLocationName = e.detail.name;
    this.selectedLocationValue = e.detail.value;
    this.sessionToSend.context.selectedHotelId = e.detail.hotelId;
    this.sessionToSend.engineId = e.detail.engineId;
  }

  @Listen('qwCalendarChange', { target: 'window' })
  public onQwCalendarChange(e: CustomEvent<SessionStayPeriod>) {
    this.sessionToSend.context.stayPeriod = e.detail;
    this.tempDates = e.detail;
    const arrivalDate = DateUtil.removeTimeFromDate(
      new Date(e.detail.arrivalDate),
    );
    const departureDate = DateUtil.removeTimeFromDate(
      new Date(e.detail.departureDate),
    );
    const formattedArrivalDate = DateUtil.formatCalendarDate(
      arrivalDate,
      sessionStorageGetItem('lang_js') || 'en',
    );
    const formattedDepartureDate = DateUtil.formatCalendarDate(
      departureDate,
      sessionStorageGetItem('lang_js') || 'en',
    );
    this.selectedDates = `${formattedArrivalDate} - ${formattedDepartureDate}`;
  }

  @Listen('roomTypeChange', { target: 'window' })
  public onQwRoomTypeChange(e: CustomEvent<QwRoomTypeType>) {
    this.closeComponents();
    this.roomTypeLabel = e.detail.label;
  }

  @Listen('qwGuestChange', { target: 'window' })
  public onQwGuestChange(e: CustomEvent) {
    this.sessionToSend.context.guests = e.detail;
    this.tempGuests = e.detail;
    let sessionTemp = cloneDeep(this.session);
    sessionTemp.context.guests = e.detail;
    this.selectedGuests = this.getGuestsLabel(sessionTemp);
  }

  public mobileOpenModalBooking() {
    this.qwOpenBookigModal.emit();
  }

  public emitGtmData() {
    if (!this.sessionSettings) {
      this.sessionSettings = getSessionSettingsFromSessionStorage();
    }
    const gtmData = {
      event: 'GA4event',
      eventName: 'booking_form_submit',
      event_data: {
        hotel_name: this.sessionSettings?.location.value,
        accomodation_type:
          this.sessionSettings?.roomType?.label ||
          sessionStorageGetItem('roomType') ||
          Language.getTranslation('allRooms'),
        adults_nb: this.sessionSettings?.guests?.adults || 1,
        children_nb: this.sessionSettings?.guests?.children || 0,
      },
    };
    this.qwEmitGtmData.emit(gtmData);
  }

  private goToOnBooked() {
    this.closeComponents();
    const baseUrl = `/${this.sessionSettings.location.value}/${this.currentLang}`;
    const redirectPage = this.isAccommodationSatisfy ? '/extras' : '/rooms';
    const redirectPath = baseUrl + redirectPage;

    if (isRunningLocally()) {
      window.location.assign('/pages' + redirectPage);
    } else {
      window.location.href = redirectPath;
    }
  }

  private onClickInput(compontentType: QwBookinBarInputType) {
    const component = this.el.querySelector(`${compontentType}`) as HTMLElement;
    const tagName = component?.tagName.toLowerCase();
    this.closeComponents(tagName);
    component?.classList.toggle('qw-component--show');
  }

  private getRoomTypeLabel(roomType) {
    return QW_ROOM_TYPE_LIST.find((r) => r.value === roomType)?.label;
  }

  private closeComponents(componentAlreadyOpenType?: string) {
    this.el.querySelectorAll(`.qw-component--show`).forEach((el) => {
      if (el.tagName.toLowerCase() != componentAlreadyOpenType) {
        el.classList.remove('qw-component--show');
      }
    });
  }

  public getSingularLabel(guestNumber) {
    return parseInt(guestNumber) === 1
      ? Language.getTranslation('guest')
      : Language.getTranslation('guests');
  }

  private book() {
    this.closeComponents();
    removeFusionIdFromSessionStorage();
    const hotelId = this.sessionToSend.context.selectedHotelId;
    const location = this.qwBookingBarDefaultLocation.hostel.find(
      (l) => Number(l.hotel_id) == hotelId,
    );

    setBookingInProgress({
      hotelValue: location.value,
    });

    if (isRunningLocally()) {
      //set it without create deeeplink
      setSessionSettingsToSessionStorage(
        {
          guests: this.sessionToSend.context.guests,
          stayPeriod: this.sessionToSend.context.stayPeriod,
        },
        this.sessionToSend.context.selectedHotelId,
      );

      window.location.assign('/pages/rooms');
    }
    if (!isInFunnelPage() && !isRunningLocally()) {
      const url = createDeepLink({
        property: location.value,
        guests: this.sessionToSend.context.guests,
        stayPeriod: this.sessionToSend.context.stayPeriod,
        lang: sessionStorage.getItem('lang_js') || 'en',
        promoCode: sessionStorage.getItem('promoCode'),
      });

      window.location.assign(url);
      return;
    }
  }

  render() {
    return (
      <Host
        class={`
        ${this.isBooked && !isInGroup() ? 'booking--searched' : ''}
      `}
      >
        {this.qwBookingBarMessage && !IS_MOBILE && (
          <div class="qw-modal--message">
            <p class="qw-modal--message__text">{this.qwBookingBarMessage}</p>
          </div>
        )}
        {!IS_MOBILE && (
          <div class="qw-booking-bar__input-fields">
            {this.isBooked && !isInGroup() && (
              <div class="qw-booking-bar__label--search">
                <p>{Language.getTranslation('yourSearch')}</p>
              </div>
            )}

            <qw-input
              qwSpanEditable={true}
              onClick={() => this.onClickInput(QwBookinBarInputType.Location)}
              qwInputIsReadonly={true}
              qwInputLabel={Language.getTranslation('destination')}
              qwInputHasSelectArrow={true}
              qwInputValue={
                this.selectedLocationName ||
                Language.getTranslation('pleaseSelect')
              }
            />
            <qw-input
              qwSpanEditable={true}
              onClick={() => this.onClickInput(QwBookinBarInputType.Date)}
              qwInputIsReadonly={true}
              qwInputHasSelectArrow={true}
              qwInputLabel={Language.getTranslation('dates')}
              qwInputValue={this.selectedDates}
            />
            <qw-input
              qwSpanEditable={true}
              onClick={() => this.onClickInput(QwBookinBarInputType.Guest)}
              qwInputIsReadonly={true}
              qwInputLabel={Language.getTranslation('guests')}
              qwInputHasSelectArrow={true}
              qwInputValue={this.selectedGuests}
            />

            <qw-input
              onClick={() => this.onClickInput(QwBookinBarInputType.RoomType)}
              qwInputIsReadonly={true}
              qwSpanEditable={true}
              qwInputLabel={Language.getTranslation('roomType')}
              qwInputHasSelectArrow={true}
              qwInputValue={this.roomTypeLabel}
            />

            {this.isBooked && !isInGroup() && (
              <div
                class="qw-booking-bar__edit"
                onClick={() => this.mobileOpenModalBooking()}
              >
                <p class="qw-booking-bar__edit--text">
                  {Language.getTranslation('edit')}
                </p>
                <span class="qw-booking-bar__edit--icon"></span>
              </div>
            )}
          </div>
        )}
        {this.isBooked && IS_MOBILE && !isInGroup() && (
          <div
            class="qw-booking-bar__edit"
            onClick={() => this.mobileOpenModalBooking()}
          >
            <p class="qw-booking-bar__edit--text">
              {Language.getTranslation('edit')}
            </p>
            <span class="qw-booking-bar__edit--icon"></span>
          </div>
        )}
        {!this.isBooked && !IS_MOBILE && (
          <QwButton
            QwButtonLabel={Language.getTranslation('bookNow')}
            QwButtonDisabled={this.selectedLocationValue === DEFAULT_LOCATION}
            QwButtonOnClick={() => {
              this.emitGtmData();
              this.book();
            }}
          />
        )}
        {this.isBooked && !isInGroup() && (
          <QwButton
            QwButtonLabel={
              Language.getTranslation('cart') +
              ' / ' +
              Language.getTranslation('continueBE')
            }
            QwButtonOnClick={() => {
              this.emitGtmData();
              this.goToOnBooked();
            }}
            QwButtonClass="qw-mobile--continue"
          />
        )}
        {!this.isBooked && IS_MOBILE && (
          <QwButton
            QwButtonLabel={Language.getTranslation('bookNow')}
            QwButtonOnClick={() => {
              this.mobileOpenModalBooking();
              this.emitGtmData();
            }}
          />
        )}
      </Host>
    );
  }
}
